// @config
import { xervixApi, xervixApiMultipart } from '../config/axios.config'; // Importamos xervixApi para manejar automáticamente el token

// @constants
import { API_ROUTES } from '../constants/apiRoutes';

// @types
import { BusinessType } from '../types/business.types';
import { XervixOnboardingResponse } from '../types/onboarding.types';
import { IDocumentsProfessional, IGalleryCompany, ProfessionalServiceAvailableResponse } from "../types/professional.types";

// Submit Categories
const submitCategories = async (categoryIds: string[]): Promise<XervixOnboardingResponse> => {
  try {
    const response = await xervixApi.post(
      API_ROUTES.PROFESSIONAL.SUBMIT_CATEGORIES,
      categoryIds
    );

    const xervixResponse = response.data as XervixOnboardingResponse;
    if (xervixResponse?.nextStep) localStorage.setItem('nextStep', xervixResponse.nextStep.toString());
    return xervixResponse;
  } catch (error: any) {
    if (error.response?.data?.message === 'A professional can only have up to 2 categories.') {
      const customError = new Error('Ya has seleccionado el máximo de categorías permitidas.');
      customError.name = 'MaxCategoriesError';
      throw customError;
    }
    throw error;
  }

};

// Submit Services
const submitServices = async (serviceIds: string[]): Promise<XervixOnboardingResponse> => {
  const response = await xervixApi.post(
    API_ROUTES.PROFESSIONAL.SUBMIT_SERVICES,
    serviceIds
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '2');
  return xervixResponse;
};

// Update Location
const updateLocation = async (
  data: {
    departmentId?: number;
    departmentNameFromGM?: string;
    countryId?: number;
    countryNameFromGM?: string;
    cityId?: number;
    cityNameFromGM?: string;
    address?: string;
    locationRawGM?: string;
  },
  skipStep = false
): Promise<XervixOnboardingResponse> => {
  const response = await xervixApi.post(
    `${API_ROUTES.PROFESSIONAL.UPDATE_LOCATION}?skipStep=${skipStep}`,
    data
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '3');
  return xervixResponse;
};

// Upsert Business Info
const upsertBusinessInfo = async (data: {
  businessType: string;
  name: string;
  identification: string;
  phoneNumber: string;
  businessDescription: string;
}): Promise<XervixOnboardingResponse> => {
  const response = await xervixApi.post(
    API_ROUTES.PROFESSIONAL.UPSERT_BUSINESS_INFO,
    data
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '4');
  return xervixResponse;
};

// Upload Documents
const uploadDocuments = async (
  logo?: File,
  files?: File[],
  documentTypes?: number[],
  skipStep = false
): Promise<XervixOnboardingResponse> => {
  const formData = new FormData();

  if (!skipStep && files && documentTypes) {
    files.forEach((file, index) => {
      formData.append('files', file);
      formData.append('documentTypes', documentTypes[index].toString());
    });
  }

  const response = await xervixApiMultipart.post(
    `${API_ROUTES.PROFESSIONAL.UPLOAD_DOCUMENTS}?skipStep=${skipStep}`,
    formData
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) {
    localStorage.setItem('nextStep', '4');
  }
  return xervixResponse;
};

// Update Referral Email
const updateReferalEmail = async (referalEmail?: string): Promise<XervixOnboardingResponse> => {
  const response = await xervixApi.patch(
    API_ROUTES.PROFESSIONAL.UPDATE_REFERAL_EMAIL,
    { referalEmail }
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '5');
  return xervixResponse;
};

// Accept Terms and Conditions
const acceptTermsAndConditions = async (isAccepted: boolean): Promise<XervixOnboardingResponse> => {
  const response = await xervixApi.patch(
    API_ROUTES.PROFESSIONAL.ACCEPT_TERMS_AND_CONDITIONS,
    { isTermAndConditionAccepted: isAccepted }
  );

  const xervixResponse = response.data as XervixOnboardingResponse;
  if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '5');
  return xervixResponse;
};

// Complete Onboarding
const completeOnboarding = async (): Promise<XervixOnboardingResponse> => {
  try {
    const response = await xervixApi.post(
      API_ROUTES.PROFESSIONAL.COMPLETE_ONBOARDING,
      {}
    );
    const xervixResponse = response.data as XervixOnboardingResponse;
    if (xervixResponse?.nextStep) localStorage.setItem('nextStep', '0');
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getBusinessTypes = async (): Promise<BusinessType[]> => {
  try {
    const response = await xervixApi.get(API_ROUTES.BUSINESS_TYPES);

    if (response.status === 200) {
      return response.data.map((json: any) => ({
        businessTypeId: json.businessTypeId,
        name: json.name,
      }));
    } else {
      throw new Error('Failed to load business types');
    }
  } catch (error) {
    throw new Error('Error fetching business types');
  }
};

export const uploadDocument = async (file: File, documentType: number) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType.toString());

  try {
    const response = await xervixApiMultipart.post('/ProfessionalDocuments/upload-document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error('Failed to upload document');
  }
};

// Servicio para saltar la subida de documentos
export const skipDocumentUpload = async () => {
  try {
    const response = await xervixApi.post('/ProfessionalDocuments/skip-upload-documents');
    return response.data;
  } catch (error) {
    console.error('Error skipping document upload:', error);
    throw new Error('Failed to skip document upload');
  }
};

// Fetch available services for a professional
const fetchAvailableServices = async (): Promise<
  ProfessionalServiceAvailableResponse[]
> => {
  try {
    const response = await xervixApi.get(
      API_ROUTES.PROFESSIONAL.FETCH_AVAILABLE_SERVICES,
      {
        headers: {
          accept: "*/*",
        },
      }
    );

    if (response.status === 200) {
      const data: ProfessionalServiceAvailableResponse[] = response.data;
      return data;
    } else {
      throw new Error("Failed to load available services");
    }
  } catch (error) {
    throw new Error(`Error fetching services: ${error}`);
  }
};

const saveGalleryByCompany = async (id: string, gallery: FormData): Promise<any> => {
  try {
    const response = await xervixApiMultipart.post(API_ROUTES.PROFESSIONAL.SAVE_GALLERY(id), gallery);
    return response;
  } catch (error) {
    throw error;
  }
};

const getDocumentsByCompany = async (): Promise<IDocumentsProfessional[]> => {
  try {
    const response = await xervixApi.get(API_ROUTES.PROFESSIONAL.GET_DOCUMENTS_BY_COMPANY);
    return response.data;
  } catch (error) {
    throw error;
  }
}

const downloadDocuments = async (id: string): Promise<any> => {
  try {
    const response = await xervixApi.get(API_ROUTES.PROFESSIONAL.DOWNLOAD_DOCUMENT(id), {
      responseType: "blob",
      headers: {
        "Accept": "image/",
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

const uploadDocumentFromDashboard = async (document: FormData): Promise<XervixOnboardingResponse> => {
  try {
    const response = await xervixApiMultipart.post(`${API_ROUTES.PROFESSIONAL.UPLOAD_DOCUMENTS}`, document);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getGalleryForProfessional = async (companyId: string): Promise<IGalleryCompany[]> => {
  try {
    const response = await xervixApi.get(API_ROUTES.PROFESSIONAL.GET_GALLERY(companyId));
    return response.data;
  } catch (error) {
    throw error;
  }
}

const downloadImageGallery = async (id: string): Promise<any> => {
  try {
    const response = await xervixApi.get(API_ROUTES.PROFESSIONAL.GET_IMAGE_GALLERY(id), {
      responseType: "blob",
      headers: {
        "Accept": "image/",
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

const methodsProfessional = {
  submitCategories,
  submitServices,
  updateLocation,
  upsertBusinessInfo,
  uploadDocuments,
  updateReferalEmail,
  acceptTermsAndConditions,
  completeOnboarding,
  getBusinessTypes,
  uploadDocument,
  fetchAvailableServices,
  skipDocumentUpload,
  saveGalleryByCompany,
  getDocumentsByCompany,
  downloadDocuments,
  uploadDocumentFromDashboard,
  getGalleryForProfessional,
  downloadImageGallery
};

export default methodsProfessional;
